import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { cabinetInfo } from '../../mock';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const inputCls =
  'w-full h-12 px-0 bg-transparent text-foreground placeholder:text-foreground/35 border-0 border-b border-foreground/20 focus:outline-none focus:border-foreground transition-colors text-base';

export default function ContactV2() {
  const [form, setForm] = useState({ nom: '', email: '', telephone: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `Contact depuis le site - ${form.nom}`;
    const body = `Nom: ${form.nom}%0ATéléphone: ${form.telephone}%0AEmail: ${form.email}%0A%0AMessage:%0A${form.message}`;
    window.location.href = `mailto:${cabinetInfo.email}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <section id="contact" className="bg-background py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-12 gap-y-6 mb-16 lg:mb-20">
          <div className="md:col-span-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-foreground/55">
              07 — Nous contacter
            </span>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-foreground">
              Prenons <span className="italic">rendez-vous</span>.
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-12">
          <motion.div {...fadeUp} className="lg:col-span-4 space-y-10">
            <div>
              <h3 className="font-display text-2xl tracking-tight text-foreground inline-flex items-center gap-2">
                <Clock size={16} className="text-amber-600" aria-hidden="true" />
                Horaires
              </h3>
              <ul className="mt-4 divide-y divide-foreground/10">
                {cabinetInfo.hours.map((h, idx) => (
                  <li key={idx} className="flex justify-between py-2.5 text-sm">
                    <span className="text-foreground/85 uppercase tracking-wider text-xs font-semibold">{h.day}</span>
                    <span className="text-foreground/55 tabular-nums text-xs">{h.time}</span>
                  </li>
                ))}
                <li className="flex justify-between py-2.5 text-sm text-foreground/40">
                  <span className="uppercase tracking-wider text-xs font-semibold">Sam. & Dim.</span>
                  <span className="text-xs">Fermé</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-display text-2xl tracking-tight text-foreground">Coordonnées</h3>
              <ul className="mt-4 space-y-4 text-sm">
                <li>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cabinetInfo.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-3 text-foreground/75 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
                  >
                    <MapPin size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
                    <span>{cabinetInfo.address}</span>
                  </a>
                </li>
                {cabinetInfo.phone.map((num, i) => (
                  <li key={i}>
                    <a
                      href={`tel:${num.replace(/\s/g, '')}`}
                      className="group flex items-center gap-3 text-foreground/75 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
                    >
                      <Phone size={16} className="shrink-0 text-amber-600" aria-hidden="true" />
                      <span>{num}</span>
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href={`mailto:${cabinetInfo.email}`}
                    className="group flex items-center gap-3 text-foreground/75 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded break-all"
                  >
                    <Mail size={16} className="shrink-0 text-amber-600" aria-hidden="true" />
                    <span>{cabinetInfo.email}</span>
                  </a>
                </li>
              </ul>

              <a
                href={cabinetInfo.doctolib}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-foreground text-background text-xs font-semibold uppercase tracking-[0.2em] hover:bg-amber-500 hover:text-foreground hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 transition-all"
              >
                <Calendar size={14} aria-hidden="true" />
                Rendez-vous en ligne
                <ArrowRight size={14} aria-hidden="true" />
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-8">
            {sent ? (
              <div className="text-center py-20">
                <p className="font-display text-4xl lg:text-5xl text-foreground tracking-tight">
                  Message <span className="italic">envoyé</span>.
                </p>
                <p className="mt-4 text-base text-foreground/65">
                  Nous vous répondrons dans les plus brefs délais.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="cv2-nom" className="block text-[11px] font-semibold uppercase tracking-[0.25em] text-foreground/55 mb-2">
                      Nom & prénom *
                    </label>
                    <input
                      id="cv2-nom"
                      type="text"
                      required
                      autoComplete="name"
                      value={form.nom}
                      onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                      placeholder="Jean Dupont"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="cv2-tel" className="block text-[11px] font-semibold uppercase tracking-[0.25em] text-foreground/55 mb-2">
                      Téléphone
                    </label>
                    <input
                      id="cv2-tel"
                      type="tel"
                      autoComplete="tel"
                      value={form.telephone}
                      onChange={(e) => setForm((p) => ({ ...p, telephone: e.target.value }))}
                      placeholder="06 XX XX XX XX"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="cv2-email" className="block text-[11px] font-semibold uppercase tracking-[0.25em] text-foreground/55 mb-2">
                    Email *
                  </label>
                  <input
                    id="cv2-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="jean@email.com"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="cv2-msg" className="block text-[11px] font-semibold uppercase tracking-[0.25em] text-foreground/55 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="cv2-msg"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                    placeholder="Décrivez votre demande…"
                    className="w-full px-0 py-3 bg-transparent text-foreground placeholder:text-foreground/35 border-0 border-b border-foreground/20 focus:outline-none focus:border-foreground resize-vertical transition-colors text-base"
                  />
                </div>
                <button
                  type="submit"
                  className="group inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-foreground text-background text-xs font-semibold uppercase tracking-[0.2em] hover:bg-amber-500 hover:text-foreground hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 transition-all"
                >
                  Envoyer le message
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </button>
                <p className="text-[10px] uppercase tracking-[0.2em] text-foreground/40">
                  En soumettant ce formulaire, vous acceptez d'être recontacté par le cabinet.
                </p>
              </form>
            )}
          </motion.div>
        </div>

        <motion.div {...fadeUp} className="mt-16 lg:mt-20 rounded-md ring-1 ring-foreground/10 overflow-hidden">
          <iframe
            title={`Plan d'accès — ${cabinetInfo.address}`}
            src="https://maps.google.com/maps?q=75+Av.+R%C3%A9gis+Pollet+62970+Courcelles-l%C3%A8s-Lens&output=embed&z=15"
            width="100%"
            height="380"
            style={{ border: 0, display: 'block' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  );
}
